import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import { unstable_trace as trace } from "scheduler/tracing";
import { FormattedMessage } from "react-intl";
import { Loading } from "../query-response-handler";
import { useAuthDataContext } from "components/auth";
import { HTTP_ERROR_CODE_403 } from "../../../utils/constants";

const Delayed = ({ render, delay = 250 }) => {
  const [show, setShow] = useState(false);

  useEffect(
    () => {
      let timeout = setTimeout(() => {
        trace("Show loader", performance.now(), () => {
          timeout = null;
          setShow(true);
        });
      }, delay);

      return () => clearTimeout(timeout);
    },
    [delay],
  );

  return show ? render() : null;
};

const Error = () => (
  <div>
    <FormattedMessage id="common.error" />
  </div>
);

const Query = ({ query, variables, renderLoading, renderError, delay, pollInterval, children }) => {
  const { onRefreshToken } = useAuthDataContext();
  const { loading = false, error = {}, data = {}, fetchMore } = useQuery(query, { variables, pollInterval });

  if (loading) {
    return <Delayed render={renderLoading} delay={delay} />;
  }

  if (error) {
    if (error.networkError?.statusCode === HTTP_ERROR_CODE_403) {
      onRefreshToken();
      return;
    }

    console.error(error);

    return renderError(error);
  }

  return children({ data, fetchMore });
};

Query.defaultProps = {
  delay: 250,

  // eslint-disable-next-line react/display-name
  renderLoading: () => <Loading />,

  // eslint-disable-next-line react/display-name
  renderError: () => <Error />,
};

Query.propTypes = {
  /**
   * Consulta graphql a ejecutar
   */
  query: PropTypes.object.isRequired,

  /**
   * Render prop para la carga de datos. Existe un retardo `delay`
   * antes del renderizado (para evitar flashing en la pantalla).
   */
  renderLoading: PropTypes.func,

  /**
   *
   */
  renderError: PropTypes.func,

  /**
   * Espera antes de renderizar el componente de carga
   */
  delay: PropTypes.number,

  /**
   * Objeto con los parámetros de la consulta
   */
  variables: PropTypes.object,

  /**
   * Intervalo de consulta
   */
  pollInterval: PropTypes.number,

  /**
   * Hijos como función. Recibe los datos recuperados tras ejecutar la consulta
   */
  children: PropTypes.func,
};

export default Query;
